import { createRouter } from '@tanstack/react-router';
import { Route as rootRoute } from './routes/__root.js';
import { Route as indexRoute } from './routes/index.js';
import { Route as loginRoute } from './routes/login.js';
import { Route as forgotPasswordRoute } from './routes/forgot-password.js';
import { Route as resetPasswordRoute } from './routes/reset-password.js';
import { Route as signupRoute } from './routes/signup.js';
import { Route as signupAgencyRoute } from './routes/signup-agency.js';
import { Route as verifyEmailRoute } from './routes/verify-email.js';
import { Route as termsRoute } from './routes/terms.js';
import { Route as privacyRoute } from './routes/privacy.js';
import { Route as appRoute } from './routes/app.js';
import { Route as dashboardRoute } from './routes/app/index.js';
import { Route as searchRoute } from './routes/app/search.js';
import { Route as shortlistRoute } from './routes/app/shortlist.js';
import { Route as categoryRoute } from './routes/app/courses/category.js';
import { Route as subCategoryRoute } from './routes/app/courses/sub-category.js';
import { Route as courseDetailRoute } from './routes/app/courses/course-detail.js';
import { Route as adminProviderListRoute } from './routes/app/admin/providers/provider-list.js';
import { Route as adminProviderNewRoute } from './routes/app/admin/providers/provider-new.js';
import { Route as adminProviderEditRoute } from './routes/app/admin/providers/provider-edit.js';
import { Route as adminCourseListRoute } from './routes/app/admin/courses/course-list.js';
import { Route as adminCourseNewRoute } from './routes/app/admin/courses/course-new.js';
import { Route as adminCourseEditRoute } from './routes/app/admin/courses/course-edit.js';
import { Route as adminChecklistRoute } from './routes/app/admin/checklist/checklist-editor.js';
import { Route as privateCourseListRoute } from './routes/app/admin/private-courses/private-course-list.js';
import { Route as privateCourseNewRoute } from './routes/app/admin/private-courses/private-course-new.js';
import { Route as privateCourseEditRoute } from './routes/app/admin/private-courses/private-course-edit.js';
import { Route as teamListRoute } from './routes/app/admin/team/team-list.js';
import { Route as brandingSettingsRoute } from './routes/app/admin/branding/branding-settings.js';
import { Route as categorySettingsRoute } from './routes/app/admin/categories/category-settings.js';
import { Route as emailSenderSettingsRoute } from './routes/app/admin/email-sender/email-sender-settings.js';
import { Route as tenantsListRoute } from './routes/app/admin/tenants/tenants-list.js';
import { Route as tenantSharingRoute } from './routes/app/admin/tenants/$tenantId/sharing.js';
import { Route as agenciesListRoute } from './routes/app/admin/agencies/agencies-list.js';
import { Route as adminUsersRoute } from './routes/app/admin/users/users.js';
import { Route as staffListRoute } from './routes/app/admin/staff/staff-list.js';
import { Route as analyticsRoute } from './routes/app/admin/analytics/analytics-dashboard.js';
import { Route as auditLogRoute } from './routes/app/admin/audit-log/audit-log-list.js';
import { Route as settingsRoute } from './routes/app/settings.js';
import { Route as suggestRoute } from './routes/app/suggest.js';
import { Route as billingRoute } from './routes/app/admin/billing/billing.js';
import { Route as pricingRoute } from './routes/app/admin/pricing/pricing-list.js';
import { Route as commissionRoute } from './routes/app/admin/commission/commission-list.js';
import { Route as consultantPricingRoute } from './routes/app/pricing.js';
import { Route as quotesListRoute } from './routes/app/quotes/quotes-list.js';
import { Route as quoteNewRoute } from './routes/app/quotes/quote-new.js';
import { Route as quoteDetailRoute } from './routes/app/quotes/quote-detail.js';
import { Route as commissionEarningsRoute } from './routes/app/commission/earnings.js';
import { Route as oversightQuotesRoute } from './routes/app/oversight/quotes-overview.js';
import { Route as oversightQuoteDetailRoute } from './routes/app/oversight/quote-detail.js';
import { Route as oversightCatalogueRoute } from './routes/app/oversight/catalogue-overview.js';

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  signupRoute,
  // Public self-serve agency signup (Phase 10, Stage 2)
  signupAgencyRoute,
  verifyEmailRoute,
  termsRoute,
  privacyRoute,
  appRoute.addChildren([
    dashboardRoute,
    searchRoute,
    suggestRoute,
    shortlistRoute,
    // Course pricing read-only view (consultant only — guarded in the route's beforeLoad)
    consultantPricingRoute,
    // Quotes (consultant + admin — guarded in each route's beforeLoad). `new` is
    // a static segment, which TanStack ranks above the dynamic `$quoteId`.
    quotesListRoute,
    quoteNewRoute,
    quoteDetailRoute,
    // Commission earnings — read-only report (super_admin/admin/owner/accounts,
    // guarded in the route's beforeLoad on view_commission)
    commissionEarningsRoute,
    // Oversight — owner/accounts read-only cross-branch screens (guarded in each
    // route's beforeLoad on view_all_branches / view_pricing)
    oversightQuotesRoute,
    // Read-only cross-agency quote detail (super_admin) + tenant owner/accounts
    oversightQuoteDetailRoute,
    oversightCatalogueRoute,
    categoryRoute,
    subCategoryRoute,
    courseDetailRoute,
    // Admin (super_admin only — guarded in each route's beforeLoad)
    adminProviderListRoute,
    adminProviderNewRoute,
    adminProviderEditRoute,
    adminCourseListRoute,
    adminCourseNewRoute,
    adminCourseEditRoute,
    // Master checklist editor (super_admin only — guarded in the route's beforeLoad)
    adminChecklistRoute,
    // Private courses (admin only — guarded in each route's beforeLoad)
    privateCourseListRoute,
    privateCourseNewRoute,
    privateCourseEditRoute,
    // Team (admin only — guarded in the route's beforeLoad)
    teamListRoute,
    // Branding (admin only — guarded in the route's beforeLoad)
    brandingSettingsRoute,
    // Course categories renaming (admin only — guarded in the route's beforeLoad)
    categorySettingsRoute,
    // Email sender (admin only — guarded in the route's beforeLoad)
    emailSenderSettingsRoute,
    // Tenant sharing (super_admin only — guarded in each route's beforeLoad)
    tenantsListRoute,
    tenantSharingRoute,
    // Agencies (super_admin only — guarded in the route's beforeLoad)
    agenciesListRoute,
    // Users — super-admin lookup + 2FA-reset escape hatch (guarded in beforeLoad)
    adminUsersRoute,
    // Staff — super-admin role + capability control centre (guarded in beforeLoad)
    staffListRoute,
    // Reporting (admin + super_admin — guarded in each route's beforeLoad)
    analyticsRoute,
    auditLogRoute,
    // Billing (admin only — guarded in the route's beforeLoad)
    billingRoute,
    // Course pricing (admin + super_admin — guarded in the route's beforeLoad)
    pricingRoute,
    // Commission — college deals + per-course special deals (admin + super_admin,
    // guarded in the route's beforeLoad)
    commissionRoute,
    // Personal account settings (any signed-in user)
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
